/**
 * Calculator Container
 *
 * Owns: calculation engine, summary bar, accordion sections, unit toggle,
 * the Clear All action, validation, and the post-submit results view.
 *
 * Does NOT own: submit button or eloqua form. Those live in sibling
 * components (`calculator/submit-button`, `calculator/eloqua-modal`) that
 * are dropped into the calculator parsys and communicate with this
 * controller via:
 *   element.closest('.calc-container').calcController
 * which exposes: validate(), resetAll(), buildResults(),
 *                encodeCompactFormPayloadString(), showResultsView().
 * After the results hero Contact `<a href="#gate-…">` is injected,
 * `reloadGatedContentForHeroContact` runs `gcdc('loadGates')` so GatedContent
 * binds the link (see basepage `page-utils.js` initial `loadGates` on load).
 *
 * Per-sub-component code lives alongside its component:
 *   • input/clientlibs/site/js/input.js   — formatInputField, formatInputsOnLoad,
 *                                           initInputTooltips
 *   • slider/clientlibs/site/js/slider.js — initSliders, initSliderTooltips,
 *                                           updateSliderFill, updateSliderDisplay
 *   • container/clientlibs/site/js/format.js — formatNumber (shared by all)
 *
 * The Multiselect (calculator/multiselect) is CSS-only at the component level;
 * all of its behavior (value collection, validation, results, reset, completion,
 * share-URL prefill, max-selection enforcement) is coordinated here alongside
 * the other field families.
 *
 * The CalculatorContainer class itself stays consolidated here because it
 * coordinates state across every sub-component atomically (validation
 * spans inputs, accordion has a single-open invariant, calculation walks
 * the entire field set, share payloads dispatch to inputs+sliders, etc.)
 * — splitting the controller would fragment that state.
 */
import { Component, registerComponent } from 'js/component.js';
import lzString from 'lz-string';
import { formatNumber } from './format.js';
import { formatInputField, formatInputsOnLoad, initInputTooltips } from '../../../../input/clientlibs/site/js/input.js';
import {
    initSliders, updateSliderFill, updateSliderDisplay, initSliderTooltips
} from '../../../../slider/clientlibs/site/js/slider.js';

const COMPONENT = 'calc-container';

/* Payload framing for the URL-hash share link. We use ASCII control
   characters (RS / US) instead of `&`/`=` so the LZ pass compresses the
   field separators down to almost nothing — they appear once each in the
   compressed dictionary. */
const PAYLOAD_FIELD_SEP = '\x1E';
const PAYLOAD_KV_SEP = '\x1F';

/* Soft warning threshold for the encoded payload length. Browsers and
   most servers handle URLs up to ~8KB; we warn well below that so calculator
   authors notice if their field set has bloated. */
const PAYLOAD_WARN_LENGTH = 2000;

/* Share-URL hash format:
       #cid=<containerId>;<lz-payload>
   The `cid=` prefix tells the Calculator Selector and every other
   container on the page WHICH calculator the encoded values belong to,
   so multi-calculator pages prefill exactly one container. The `;` is
   safe — it's not a URL hash terminator and the LZ-compressed payload
   never emits one. Hashes without the prefix (legacy share URLs) are
   still accepted: every container tries to apply them. */
const HASH_CID_PREFIX = 'cid=';
const HASH_CID_SEP = ';';

export class CalculatorContainer extends Component {

    /* ───── State ───── */

    state = {
        displayMode: this.element.getAttribute('data-display-mode') || 'realtime',
        activeUnit: this.element.getAttribute('data-active-unit') || '',
    };

    /* ───── Init ───── */

    init() {
        this.locale = document.documentElement.lang || 'en-US';

        this.element.calcController = this;
        this.isPrefilled = false;
        this.containerId = (this.element.getAttribute('data-container-id') || '').trim();

        /* Cache DOM lookups first so every helper below can rely on
           `this.elements` (loadConversions / autoSwitchToAlt included). */
        this.cacheElements();
        this.hideEmptySectionTotals();
        this.loadConversions();
        this.resolveConversionTokens();
        this.assignShareIds();
        this.populateSectionCategoryLabels();
        this.setStickyOffset();
        this.addEventListeners();
        initInputTooltips(this.element);
        initSliderTooltips(this.element);
        this.setupNextButtons();
        this.setInitialSectionState();
        initSliders(this.elements.sliders, this.locale);
        formatInputsOnLoad(this.elements.inputs, this.locale);

        /* In `realtime` mode this evaluates totals + section completion;
           in `onsubmit` mode it just updates the green-check state so
           sections that already have non-zero defaults light up on load
           without revealing the deferred totals. Same dispatch as
           `onInputChange()`. */
        this.onInputChange();

        /* MUST run AFTER inputs/sliders are wired and share IDs assigned so the
           prefill can find each field's wrapper by its stable share ID. */
        this.applyHashPayload();
    }

    /**
     * Compute and stamp a stable, deterministic share ID on every input
     * and slider wrapper. The ID is a short base36 hash of the JCR
     * `data-resource-path` attribute the server emitted, which means:
     *   • Same value for every visiting user (no UUID, no random seed).
     *   • Survives field-name renames (path stays the same).
     *   • Compact (~7 chars) so the URL-hash payload stays small.
     *   • No authoring burden — the path is automatic.
     *
     * Falls back to the legacy `data-field-name` if no resource path is
     * present (unlikely in normal AEM rendering but keeps unit tests and
     * standalone HTML demos working).
     */
    assignShareIds() {
        const wrappers = this.element.querySelectorAll('.calc-input[data-resource-path], .calc-slider[data-resource-path], .calc-multiselect[data-resource-path], .calc-input[data-field-name], .calc-slider[data-field-name], .calc-multiselect[data-field-name]');
        wrappers.forEach((wrapper) => {
            if (wrapper.getAttribute('data-share-id')) return;
            const path = wrapper.getAttribute('data-resource-path') || wrapper.getAttribute('data-field-name') || '';
            if (!path) return;
            wrapper.setAttribute('data-share-id', CalculatorContainer.stableHash(path));
        });
    }

    /**
     * Resolve the human-readable category label shown beneath each
     * section title (Figma nodes 249:4779 collapsed / 251:5800 open) by
     * matching the section's `data-category` to a Summary Bar item's
     * `data-summary-field`. The matched item's `data-summary-label`
     * (e.g. "Revenue Growth") is written into the section's
     * `[data-category-label-target]` element.
     *
     * If no summary item matches, the HTL's fallback (the raw category
     * key) stays in place — better than rendering an empty row, and
     * authors will notice the machine name and fix the mismatch.
     */
    populateSectionCategoryLabels() {
        const labelByField = {};
        this.elements.summaryValues.forEach((el) => {
            const field = (el.getAttribute('data-summary-field') || '').trim();
            const label = (el.getAttribute('data-summary-label') || '').trim();
            if (field && label) labelByField[field] = label;
        });

        this.elements.sections.forEach((section) => {
            const target = section.querySelector('[data-category-label-target]');
            if (!target) return;
            const key = (section.getAttribute('data-category') || '').trim();
            const label = labelByField[key];
            if (label) target.textContent = label;
        });
    }

    /**
     * 32-bit FNV-1a hash returned as a base36 string. Pure function:
     * the same input always produces the same output, regardless of
     * browser, locale, or visitor — exactly what we need for share IDs.
     * Output is ~7 chars and URL-safe with no escaping required.
     *
     * FNV-1a is defined in terms of bitwise XOR (`^=`) and an unsigned
     * right-shift (`>>> 0`) to coerce the signed result back to a 32-bit
     * unsigned int — there's no algebraic substitute, so the `no-bitwise`
     * lint is suppressed for this function only.
     */
    /* eslint-disable no-bitwise */
    static stableHash(str) {
        let h = 0x811c9dc5;
        for (let i = 0; i < str.length; i += 1) {
            h ^= str.charCodeAt(i);
            h = Math.imul(h, 0x01000193);
        }
        return (h >>> 0).toString(36);
    }
    /* eslint-enable no-bitwise */

    setStickyOffset() {
        const header = document.querySelector('header, .header, [role="banner"], .site-header');
        if (header) {
            const { height } = header.getBoundingClientRect();
            this.element.style.setProperty('--calc-sticky-top', `${height}px`);
        }
    }

    /**
     * Evaluate summary bar formulas. Each summary item has a data-formula
     * attribute configured in the container dialog.
     */
    updateSummaryBar() {
        const values = this.collectValues();
        this.elements.summaryValues.forEach((el) => {
            const formula = el.getAttribute('data-formula');
            if (!formula) return;
            const result = this.evaluateFormula(formula, values);
            el.textContent = this.formatValue(result, el); // eslint-disable-line no-param-reassign

            const fieldName = el.getAttribute('data-summary-field');
            if (fieldName) {
                values[fieldName] = result;
            }
            // Cache the numeric result on the element so the results view can read it
            el.setAttribute('data-numeric-value', String(result));
        });
    }

    cacheElements() {
        this.elements = {
            formView: this.element.querySelector('.calc-container__form-view'),
            resultsView: this.element.querySelector('.calc-container__results-view'),
            sections: Array.from(this.element.querySelectorAll('.calc-section')),
            inputs: Array.from(this.element.querySelectorAll('.calc-input__field')),
            sliders: Array.from(this.element.querySelectorAll('.calc-slider__range')),
            multiselects: Array.from(this.element.querySelectorAll('.calc-multiselect')),
            displays: Array.from(this.element.querySelectorAll('.calc-display')),
            summaryValues: Array.from(this.element.querySelectorAll('[data-summary-field]')),
            unitToggleBtns: Array.from(this.element.querySelectorAll('.calc-unit-toggle__btn')),
            clearAllBtn: this.element.querySelector('.calc-clear-btn'),
        };
    }

    /* ───── Event Listeners ───── */

    addEventListeners() {
        this.elements.inputs.forEach((input) => {
            input.addEventListener('input', () => this.onInputChange());

            input.addEventListener('blur', () => formatInputField(input, this.locale));

            input.addEventListener('focus', () => {
                const raw = input.getAttribute('data-raw-value');
                if (raw !== null) {
                    input.type = 'number'; // eslint-disable-line no-param-reassign
                    input.value = raw; // eslint-disable-line no-param-reassign
                    input.removeAttribute('data-raw-value');
                }
                if (parseFloat(input.value) === 0) {
                    input.select();
                }
            });

            /* Browsers treat the mouse wheel as up/down arrow on a focused
               `<input type="number">`, which silently changes the visitor's
               authored value while they're trying to scroll the page.
               Suppress the wheel only while the input is actively focused
               so normal page scrolling over an unfocused field still works. */
            input.addEventListener('wheel', (e) => {
                if (document.activeElement === input) {
                    e.preventDefault();
                }
            }, { passive: false });
        });

        this.elements.sliders.forEach((slider) => {
            slider.addEventListener('input', () => {
                updateSliderFill(slider);
                updateSliderDisplay(slider, this.locale);
                this.onInputChange();
            });
        });

        this.elements.multiselects.forEach((group) => {
            this.enforceMaxSelections(group);
            group.addEventListener('change', () => {
                this.enforceMaxSelections(group);
                this.onInputChange();
            });
        });

        this.elements.sections.forEach((section) => {
            const header = section.querySelector('.calc-section__header');
            /* Native <button> handles Space/Enter activation — no extra
               keydown listener needed (and adding one would double-fire
               the toggle on Space, which collapses what we just opened). */
            if (header) {
                /* `header` is a native <button>, so Space / Enter fire
                   `click` automatically — no custom keydown needed. */
                header.addEventListener('click', () => this.toggleSection(section));
            }
        });

        this.elements.unitToggleBtns.forEach((btn) => {
            btn.addEventListener('click', () => this.switchUnit(btn.getAttribute('data-unit-key')));
        });

        if (this.elements.clearAllBtn) {
            this.elements.clearAllBtn.addEventListener('click', () => this.resetAll());
        }
    }

    /* ───── Accordion ─────
       Single-open behavior: at most one section is expanded at a time.
       Clicking an already-open header collapses it (zero open is allowed,
       e.g. when the user wants to scroll past the form). The first section
       is opened on init by `setInitialSectionState()`, and the per-section
       "Next" button advances across all sections via `setupNextButtons()`. */

    toggleSection(section) {
        const isOpen = section.classList.contains('calc-section--open');
        if (isOpen) {
            this.closeSection(section);
        } else {
            this.openSection(section);
        }
    }

    openSection(section) {
        if (!section) return;
        this.elements.sections.forEach((other) => {
            if (other !== section) this.closeSection(other);
        });
        section.classList.add('calc-section--open');
        const header = section.querySelector('.calc-section__header');
        if (header) header.setAttribute('aria-expanded', 'true');
    }

    closeSection(section) {
        if (!section) return;
        section.classList.remove('calc-section--open');
        const header = section.querySelector('.calc-section__header');
        if (header) header.setAttribute('aria-expanded', 'false');
    }

    /**
     * Force the first section open and the rest closed on initial load.
     * Authors can mark multiple sections as `initiallyExpanded` via the
     * dialog, but the single-open invariant takes precedence — we honor
     * the visual contract the user requested ("by default the first one
     * should be open") regardless of authoring.
     */
    setInitialSectionState() {
        const { sections } = this.elements;
        if (!sections || !sections.length) return;
        sections.forEach((section, index) => {
            if (index === 0) this.openSection(section);
            else this.closeSection(section);
        });
    }

    /**
     * Wire each section's "Next" button to advance to the subsequent
     * section in DOM order. The very last section in the list has no
     * "next", so we hide its button entirely.
     */
    setupNextButtons() {
        const { sections } = this.elements;
        if (!sections || !sections.length) return;
        sections.forEach((section, index) => {
            const btn = section.querySelector('.calc-section__next');
            if (!btn) return;
            const nextSection = sections[index + 1];
            if (!nextSection) {
                btn.hidden = true;
                return;
            }
            btn.addEventListener('click', () => {
                this.openSection(nextSection);
                /* Smoothly bring the newly opened section into view. The
                   sticky page header is accounted for by `scroll-margin-top`
                   on `.calc-section` (set via the `--calc-sticky-top` CSS
                   var managed in `setStickyOffset()`). */
                nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    }

    /* ───── Calculation Engine ───── */

    onInputChange() {
        if (this.state.displayMode === 'realtime') {
            this.calculate();
            return;
        }
        /* Onsubmit mode — totals are deferred to submit, but the section
           completion indicator (green check) should still update live so
           the visitor gets immediate feedback as they fill each section. */
        this.elements.sections.forEach((section) => this.markSectionCompletion(section));
    }

    calculate() {
        const values = this.collectValues();

        this.elements.displays.forEach((display) => {
            const formula = display.getAttribute('data-formula');
            if (!formula) return;

            const result = this.evaluateFormula(formula, values);
            const fieldName = display.getAttribute('data-field-name');
            const valueEl = display.querySelector('.calc-display__value');

            if (valueEl) {
                valueEl.textContent = this.formatValue(result, display);
            }
            display.setAttribute('data-numeric-value', String(result));
            if (fieldName) {
                values[fieldName] = result;
            }
        });

        this.updateSectionTotals();
        this.updateSummaryBar();
    }

    collectValues() {
        const values = {};

        this.elements.inputs.forEach((input) => {
            const raw = input.getAttribute('data-raw-value');
            values[input.name] = parseFloat(raw !== null ? raw : input.value) || 0;
        });

        this.elements.sliders.forEach((slider) => {
            values[slider.name] = parseFloat(slider.value) || 0;
        });

        /* A multiselect contributes the SUM of its checked options' weights
           (Option Value, or 1 when blank) so it can be referenced in formulas
           like a numeric input — e.g. {maturityScore} = {q1} + {q2}. */
        this.elements.multiselects.forEach((group) => {
            const name = group.getAttribute('data-field-name');
            if (!name) return;
            values[name] = CalculatorContainer.multiselectSum(group);
        });

        this.elements.displays.forEach((display) => {
            const fieldName = display.getAttribute('data-field-name');
            if (fieldName) {
                values[fieldName] = parseFloat(display.getAttribute('data-numeric-value')) || 0;
            }
        });

        // Built-in variables: {__unitFactor} and {__convName_factor} for each conversion
        const isAlt = this.state.activeUnit && this.state.activeUnit !== this.getDefaultUnitKey();
        if (this.conversions) {
            Object.entries(this.conversions).forEach(([name, conv]) => {
                values[`__${name}_factor`] = isAlt ? conv.factor : 1; // eslint-disable-line no-underscore-dangle
            });
            const firstConv = Object.values(this.conversions)[0];
            values.__unitFactor = isAlt && firstConv ? firstConv.factor : 1; // eslint-disable-line no-underscore-dangle
        }

        return values;
    }

    /**
     * Safe formula evaluator. Parses {fieldName} tokens and evaluates
     * arithmetic expressions using only +, -, *, /, (, ), and numbers.
     */
    evaluateFormula(formula, values) {
        const expr = formula.replace(/\{([^}]+)\}/g, (match, fieldName) => {
            const val = values[fieldName.trim()];
            return typeof val === 'number' && !Number.isNaN(val) ? val : 0;
        });

        if (!/^[\d\s+\-*/().]+$/.test(expr)) {
            return 0;
        }

        try {
            // eslint-disable-next-line no-new-func
            const result = new Function(`"use strict"; return (${expr});`)();
            return typeof result === 'number' && Number.isFinite(result) ? result : 0;
        } catch (e) {
            return 0;
        }
    }

    /**
     * Format a number with prefix/suffix from the element's data attributes
     * and the number format preset from data-format. (The pure
     * `formatNumber` helper lives in `./format.js` so the input/slider
     * sub-components can use it without importing this controller.)
     */
    formatValue(n, el) {
        const prefix = (el && (el.getAttribute('data-resolved-prefix') || el.getAttribute('data-value-prefix'))) || '';
        const suffix = (el && (el.getAttribute('data-resolved-suffix') || el.getAttribute('data-value-suffix'))) || '';
        const formatKey = (el && el.getAttribute('data-format')) || 'integer';
        const formatted = formatNumber(n, formatKey, this.locale);
        return `${prefix}${formatted}${suffix}`;
    }

    /* ───── Aggregation ───── */

    /**
     * Hide the accordion header's running-total (`$0`) for any section that
     * has no section-total Display to feed it. Numeric ROI sections always
     * author a `[data-section-total="true"]` Display, so their totals stay;
     * qualitative sections (e.g. a self-assessment built from Multiselects
     * only) have none, and a permanent "$0" in the header would be
     * meaningless — so it's removed.
     */
    hideEmptySectionTotals() {
        this.elements.sections.forEach((section) => {
            const totalEl = section.querySelector('[data-section-total]');
            if (!totalEl) return;
            if (!section.querySelector('.calc-display[data-section-total="true"]')) {
                totalEl.hidden = true;
            }
        });
    }

    updateSectionTotals() {
        this.elements.sections.forEach((section) => {
            const totalEl = section.querySelector('[data-section-total]');
            if (totalEl) {
                const sectionTotalDisplay = section.querySelector('.calc-display[data-section-total="true"]');
                if (sectionTotalDisplay) {
                    const valueEl = sectionTotalDisplay.querySelector('.calc-display__value');
                    if (valueEl) {
                        totalEl.textContent = valueEl.textContent;
                    }
                }
            }
            this.markSectionCompletion(section);
        });
    }

    /**
     * Toggle the `calc-section--completed` class on the given section. The
     * class drives the green check that appears next to the title in the
     * closed-row Figma treatment (nodes 249:4779 / 249:4764).
     *
     * Two completion signals depending on display mode:
     *   • realtime — the section's `[data-section-total="true"]` display
     *     evaluated to a non-zero number. Matches what the user sees in
     *     the row's value column.
     *   • onsubmit — the section total isn't computed until submit, so we
     *     fall back to a "looks filled" check: every input and slider in
     *     the section has a finite, non-zero value. This lets the green
     *     check appear as the visitor fills each section even though the
     *     row still shows $0 until submit.
     */
    markSectionCompletion(section) {
        let isCompleted = false;

        if (this.state.displayMode === 'onsubmit') {
            const fields = section.querySelectorAll('.calc-input__field, .calc-slider__range');
            const groups = section.querySelectorAll('.calc-multiselect');
            if (fields.length > 0 || groups.length > 0) {
                const fieldsOk = Array.from(fields).every((field) => {
                    const raw = field.getAttribute('data-raw-value');
                    const val = raw !== null ? raw : field.value;
                    const num = parseFloat(val);
                    return Number.isFinite(num) && num !== 0;
                });
                /* A group counts as filled once it meets its min (≥1 by default
                   for both required and optional groups, so a qualitative
                   "select all that apply" section still lights its check). */
                const groupsOk = Array.from(groups).every((group) => {
                    const { min, checked } = CalculatorContainer.multiselectStats(group);
                    return checked >= Math.max(min, 1);
                });
                isCompleted = fieldsOk && groupsOk;
            }
        } else {
            const sectionTotalDisplay = section.querySelector('.calc-display[data-section-total="true"]');
            if (sectionTotalDisplay) {
                const numericTotal = parseFloat(sectionTotalDisplay.getAttribute('data-numeric-value'));
                isCompleted = Number.isFinite(numericTotal) && numericTotal !== 0;
            }
        }

        section.classList.toggle('calc-section--completed', isCompleted);
    }

    /* ───── Named Conversions ───── */

    /**
     * Built-in Imperial → Metric conversion pairs.
     */
    static IMPERIAL_METRIC = {
        weight: { defaultSuffix: ' lb', altSuffix: ' kg', factor: 0.453592 },
        length: { defaultSuffix: ' in', altSuffix: ' cm', factor: 2.54 },
        height: { defaultSuffix: ' ft', altSuffix: ' m', factor: 0.3048 },
        distance: { defaultSuffix: ' mi', altSuffix: ' km', factor: 1.60934 },
        volume: { defaultSuffix: ' gal', altSuffix: ' L', factor: 3.78541 },
        smallVolume: { defaultSuffix: ' oz', altSuffix: ' mL', factor: 29.5735 },
        temperature: {
            defaultSuffix: ' °F', altSuffix: ' °C', factor: 0, toAltFormula: '({value} - 32) * 5 / 9', toDefaultFormula: '({value} * 9 / 5) + 32'
        },
    };

    loadConversions() {
        this.conversions = {};
        const unitSystem = this.element.getAttribute('data-unit-system') || 'none';

        if (unitSystem === 'imperial-metric') {
            Object.entries(CalculatorContainer.IMPERIAL_METRIC).forEach(([name, conv]) => {
                this.conversions[name] = {
                    defaultPrefix: conv.defaultPrefix || '',
                    defaultSuffix: conv.defaultSuffix || '',
                    altPrefix: conv.altPrefix || '',
                    altSuffix: conv.altSuffix || '',
                    factor: conv.factor,
                    toAltFormula: conv.toAltFormula || '',
                    toDefaultFormula: conv.toDefaultFormula || '',
                };
            });
        }

        this.element.querySelectorAll('.calc-conversion').forEach((el) => {
            const name = el.getAttribute('data-conv-name');
            if (name) {
                this.conversions[name] = {
                    defaultPrefix: el.getAttribute('data-default-prefix') || '',
                    defaultSuffix: el.getAttribute('data-default-suffix') || '',
                    altPrefix: el.getAttribute('data-alt-prefix') || '',
                    altSuffix: el.getAttribute('data-alt-suffix') || '',
                    factor: parseFloat(el.getAttribute('data-factor')) || 1,
                    toAltFormula: el.getAttribute('data-to-alt-formula') || '',
                    toDefaultFormula: el.getAttribute('data-to-default-formula') || '',
                };
            }
        });

        if (unitSystem === 'imperial-metric') {
            const isUS = /^en-US/i.test(this.locale);
            if (!isUS) {
                this.autoSwitchToAlt();
            }
        }

        if (unitSystem === 'custom') {
            const defaultCountries = (this.element.getAttribute('data-custom-default-countries') || '').split(',').map((s) => s.trim().toLowerCase());
            if (defaultCountries.length > 0 && defaultCountries[0] !== '' && !defaultCountries.includes(this.locale.toLowerCase())) {
                this.autoSwitchToAlt();
            }
        }
    }

    autoSwitchToAlt() {
        this.state.activeUnit = 'alt';
        this.element.setAttribute('data-active-unit', 'alt');
        this.elements.unitToggleBtns.forEach((btn) => {
            const isActive = btn.getAttribute('data-unit-key') === 'alt';
            btn.classList.toggle('calc-unit-toggle__btn--active', isActive);
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    }

    resolveConversionTokens() {
        const isAlt = this.state.activeUnit && this.state.activeUnit !== this.getDefaultUnitKey();

        this.element.querySelectorAll('[data-template]').forEach((el) => {
            const template = el.getAttribute('data-template');
            const type = el.classList.contains('calc-input__suffix') || el.classList.contains('calc-slider__suffix') ? 'suffix' : 'prefix';
            el.textContent = this.resolveToken(template, isAlt, type); // eslint-disable-line no-param-reassign
        });

        this.element.querySelectorAll('[data-value-prefix], [data-value-suffix]').forEach((el) => {
            const prefixTemplate = el.getAttribute('data-value-prefix') || '';
            const suffixTemplate = el.getAttribute('data-value-suffix') || '';
            el.setAttribute('data-resolved-prefix', this.resolveToken(prefixTemplate, isAlt, 'prefix'));
            el.setAttribute('data-resolved-suffix', this.resolveToken(suffixTemplate, isAlt, 'suffix'));
        });
    }

    resolveToken(template, isAlt, type) {
        if (!template) return '';
        const match = template.match(/^\{(\w+)\}$/);
        if (!match) return template;
        const conv = this.conversions[match[1]];
        if (!conv) return template;
        if (type === 'prefix') return isAlt ? conv.altPrefix : conv.defaultPrefix;
        return isAlt ? conv.altSuffix : conv.defaultSuffix;
    }

    /* ───── Unit Toggle ───── */

    switchUnit(newUnitKey) {
        if (newUnitKey === this.state.activeUnit) return;

        const wasAlt = this.state.activeUnit !== this.getDefaultUnitKey();
        this.state.activeUnit = newUnitKey;
        this.element.setAttribute('data-active-unit', newUnitKey);
        const isAlt = newUnitKey !== this.getDefaultUnitKey();

        this.elements.unitToggleBtns.forEach((btn) => {
            const isActive = btn.getAttribute('data-unit-key') === newUnitKey;
            btn.classList.toggle('calc-unit-toggle__btn--active', isActive);
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.elements.inputs.forEach((field) => {
            const wrapper = field.closest('.calc-input');
            if (!wrapper) return;
            const prefixEl = wrapper.querySelector('.calc-input__prefix[data-template]');
            const suffixEl = wrapper.querySelector('.calc-input__suffix[data-template]');
            const template = (prefixEl && prefixEl.getAttribute('data-template'))
                             || (suffixEl && suffixEl.getAttribute('data-template')) || '';
            const match = template.match(/^\{(\w+)\}$/);
            if (!match) return;
            const convName = match[1];
            const conv = this.conversions[convName];
            if (!conv) return;

            const rawAttr = field.getAttribute('data-raw-value');
            const currentVal = parseFloat(rawAttr !== null ? rawAttr : field.value) || 0;
            let newVal = currentVal;

            if (!wasAlt && isAlt && conv.toAltFormula) {
                newVal = this.evaluateFormula(conv.toAltFormula, { value: currentVal });
            } else if (wasAlt && !isAlt && conv.toDefaultFormula) {
                newVal = this.evaluateFormula(conv.toDefaultFormula, { value: currentVal });
            } else if (conv.factor && conv.factor !== 1) {
                if (wasAlt && !isAlt) newVal = currentVal / conv.factor;
                else if (!wasAlt && isAlt) newVal = currentVal * conv.factor;
            }

            if (newVal !== currentVal) {
                const rounded = parseFloat(newVal.toFixed(4));
                field.value = rounded; // eslint-disable-line no-param-reassign
                field.setAttribute('data-raw-value', String(rounded));
                const formatWrapper = field.closest('.calc-input');
                const fk = formatWrapper ? (formatWrapper.getAttribute('data-format') || 'integer') : 'integer';
                if (fk !== 'plain' && field.type === 'text') {
                    field.value = formatNumber(rounded, fk, this.locale); // eslint-disable-line no-param-reassign
                }
            }
        });

        this.resolveConversionTokens();

        if (this.state.displayMode === 'realtime') {
            this.calculate();
        }
    }

    getDefaultUnitKey() {
        return 'default';
    }

    /* ───── Multiselect helpers ───── */

    /**
     * Sum of the checked options' weights for a `.calc-multiselect` group.
     * Each option's weight is its `data-weight` (the authored Option Value)
     * parsed as a number, defaulting to 1 when blank/non-numeric — so a plain
     * multiselect resolves to "count of checked" while authors who set numeric
     * Option Values get a weighted score.
     */
    static multiselectSum(group) {
        let sum = 0;
        group.querySelectorAll('.calc-multiselect__input:checked').forEach((cb) => {
            const w = cb.getAttribute('data-weight');
            const n = w === null || w === '' ? 1 : parseFloat(w);
            sum += Number.isFinite(n) ? n : 1;
        });
        return sum;
    }

    /**
     * Resolve a multiselect group's validation constraints + current count.
     * `min` defaults to 1 when the group is required and no explicit Min
     * Selections is authored; `max` of 0 means "no limit".
     */
    static multiselectStats(group) {
        const required = group.getAttribute('data-ms-required') === 'true';
        const minAttr = parseInt(group.getAttribute('data-min-selections'), 10);
        const maxAttr = parseInt(group.getAttribute('data-max-selections'), 10);
        const requiredMin = required ? 1 : 0;
        const min = Number.isFinite(minAttr) && minAttr > 0 ? minAttr : requiredMin;
        const max = Number.isFinite(maxAttr) && maxAttr > 0 ? maxAttr : 0;
        const checked = group.querySelectorAll('.calc-multiselect__input:checked').length;
        return {
            required, min, max, checked,
        };
    }

    /**
     * When a Max Selections limit is set and reached, disable the remaining
     * unchecked options so the visitor can't exceed it; re-enable them once a
     * selection is removed. No-op for groups without a max (or in review mode,
     * where every option is already disabled).
     */
    enforceMaxSelections(group) {
        const { max } = CalculatorContainer.multiselectStats(group);
        if (!max) return;
        const boxes = Array.from(group.querySelectorAll('.calc-multiselect__input'));
        const reachedMax = boxes.filter((cb) => cb.checked).length >= max;
        boxes.forEach((cb) => {
            if (cb.hasAttribute('data-review-disabled')) return;
            cb.disabled = reachedMax && !cb.checked; // eslint-disable-line no-param-reassign
        });
    }

    /* ───── Validation (public API) ───── */

    /**
     * Run required-field validation. Returns true when all required fields
     * have values; sibling components (e.g. submit-button) call this before
     * opening the eloqua modal.
     */
    validate() {
        const errors = [];
        this.clearValidation();

        /* Required inputs AND sliders both carry `data-required="true"`. A
           slider (the "percentage bar") reports its thumb position as its
           value, so a required slider left at 0 is treated as blank here —
           same rule as an empty/`0` number input — which is what stops a
           $0 ROI slipping through to submit. `calc-input` / `calc-slider`
           share a parallel class layout (`__error`, `--invalid`) so one
           branchless block styles either wrapper. */
        const requiredFields = this.element.querySelectorAll('[data-required="true"]');
        requiredFields.forEach((field) => {
            if (field.disabled || field.closest('[hidden]')) return;

            const val = field.value.trim();
            if (val === '' || val === '0') {
                const wrapper = field.closest('.calc-input, .calc-slider');
                if (wrapper) {
                    const base = wrapper.classList.contains('calc-slider') ? 'calc-slider' : 'calc-input';
                    wrapper.classList.add(`${base}--invalid`);

                    /* Surface the inline message + announce it to screen
                       readers. The `<p role="alert">` becomes a live
                       region the moment we remove `[hidden]`, and
                       `aria-invalid` on the field itself is what gets
                       announced when the user re-focuses the control. */
                    const errorEl = wrapper.querySelector(`.${base}__error`);
                    if (errorEl) errorEl.removeAttribute('hidden');
                }
                field.setAttribute('aria-invalid', 'true');

                const section = field.closest('.calc-section');
                errors.push({ field, section });
            }
        });

        /* Required / min / max validation for multiselect groups. Pushed into
           the same `errors` list so focus + section-opening order is shared
           with the input fields above. */
        this.elements.multiselects.forEach((group) => {
            if (group.closest('[hidden]')) return;
            const { min, max, checked } = CalculatorContainer.multiselectStats(group);
            const tooFew = min > 0 && checked < min;
            const tooMany = max > 0 && checked > max;
            if (!tooFew && !tooMany) return;

            group.classList.add('calc-multiselect--invalid');
            group.setAttribute('aria-invalid', 'true');
            const errorEl = group.querySelector('.calc-multiselect__error');
            if (errorEl) errorEl.removeAttribute('hidden');

            const firstInput = group.querySelector('.calc-multiselect__input');
            const section = group.closest('.calc-section');
            errors.push({ field: firstInput || group, section, group });
        });

        if (errors.length > 0) {
            this.focusError(errors[0]);

            errors.forEach(({ field, group }) => {
                if (group) {
                    /* Multiselect: clear the group's error on the next change
                       once its min/max constraint is satisfied. */
                    const handler = () => {
                        const { min, max, checked } = CalculatorContainer.multiselectStats(group);
                        const ok = !(min > 0 && checked < min) && !(max > 0 && checked > max);
                        if (ok) {
                            group.classList.remove('calc-multiselect--invalid');
                            group.setAttribute('aria-invalid', 'false');
                            const errorEl = group.querySelector('.calc-multiselect__error');
                            if (errorEl) errorEl.setAttribute('hidden', '');
                            group.removeEventListener('change', handler);
                        }
                    };
                    group.addEventListener('change', handler);
                    return;
                }
                /* Both `<input>` and `<input type="range">` emit `input`
                   events, so dragging a required slider off 0 clears its
                   error exactly like typing into a required number field. */
                const wrapper = field.closest('.calc-input, .calc-slider');
                const base = wrapper && wrapper.classList.contains('calc-slider') ? 'calc-slider' : 'calc-input';
                const handler = () => {
                    const val = field.value.trim();
                    if (val !== '' && val !== '0') {
                        if (wrapper) {
                            wrapper.classList.remove(`${base}--invalid`);
                            const errorEl = wrapper.querySelector(`.${base}__error`);
                            if (errorEl) errorEl.setAttribute('hidden', '');
                        }
                        field.setAttribute('aria-invalid', 'false');
                        field.removeEventListener('input', handler);
                    }
                };
                field.addEventListener('input', handler);
            });
        }

        return errors.length === 0;
    }

    clearValidation() {
        this.element.querySelectorAll('.calc-input--invalid').forEach((el) => {
            el.classList.remove('calc-input--invalid');
        });
        this.element.querySelectorAll('.calc-input__error').forEach((el) => {
            el.setAttribute('hidden', '');
        });
        this.element
            .querySelectorAll('.calc-input__field[aria-invalid="true"]')
            .forEach((el) => el.setAttribute('aria-invalid', 'false'));

        this.element.querySelectorAll('.calc-slider--invalid').forEach((el) => {
            el.classList.remove('calc-slider--invalid');
        });
        this.element.querySelectorAll('.calc-slider__error').forEach((el) => {
            el.setAttribute('hidden', '');
        });
        this.element
            .querySelectorAll('.calc-slider__range[aria-invalid="true"]')
            .forEach((el) => el.setAttribute('aria-invalid', 'false'));

        this.element.querySelectorAll('.calc-multiselect--invalid').forEach((el) => {
            el.classList.remove('calc-multiselect--invalid');
            el.setAttribute('aria-invalid', 'false');
        });
        this.element.querySelectorAll('.calc-multiselect__error').forEach((el) => {
            el.setAttribute('hidden', '');
        });
    }

    focusError({ field, section }) {
        if (section && !section.classList.contains('calc-section--open')) {
            this.openSection(section);
        }

        setTimeout(() => {
            /* Scroll the visible field wrapper into view rather than a
               visually-hidden control (e.g. a multiselect's 1px checkbox). */
            const scrollTarget = field.closest('.calc-input, .calc-slider, .calc-multiselect') || field;
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field.focus();
        }, 100);
    }

    /* ───── Reset (public API) ───── */

    /**
     * Reset every input/slider in the calculator to its starting state.
     */
    resetAll() {
        // Run on-submit calculations once so collected values are accurate
        if (this.state.displayMode === 'onsubmit') {
            this.calculate();
        }

        this.elements.inputs.forEach((el) => {
            /* Clear the field so the in-box placeholder/label re-shows
               (matches the initial empty state). Calculation logic treats
               empty as 0 via parseFloat(value) || 0. */
            el.removeAttribute('data-raw-value');
            el.type = 'number'; // eslint-disable-line no-param-reassign
            el.value = ''; // eslint-disable-line no-param-reassign
        });

        this.elements.sliders.forEach((el) => {
            el.value = el.min || 0; // eslint-disable-line no-param-reassign
            updateSliderFill(el);
            updateSliderDisplay(el, this.locale);
        });

        this.elements.multiselects.forEach((group) => {
            group.querySelectorAll('.calc-multiselect__input').forEach((cb) => {
                cb.checked = false; // eslint-disable-line no-param-reassign
            });
            this.enforceMaxSelections(group);
        });

        if (this.isPrefilled) {
            this.exitPrefilledReviewMode();
        }

        /* Restore the initial accordion state (first section open, rest
           closed) so "Start Over" returns to the top of the calculator
           instead of leaving the last-interacted section open. On
           multi-calculator pages the Selector re-initialises on re-pick;
           this covers single-calculator pages where nothing else does. */
        this.setInitialSectionState();

        this.clearValidation();
        this.calculate();
    }

    /* ───── Results building (public API) ───── */

    /**
     * Build a flat array describing the current calculator state. Used by
     * the eloqua-modal to map per-input answers into Eloqua columns AND
     * to drive the LZ-compressed share-link payload.
     *
     * Each entry:
     *   { label, value, formatted, raw, id, prefix, suffix,
     *     eloquaFieldName, includeQuestion }
     *
     *   • `label`     — question / label text shown next to the field.
     *   • `value`     — display string with prefix+suffix (e.g. `"$ 1,000"`).
     *   • `formatted` — value formatted per the field's number-format,
     *                   without prefix/suffix (e.g. `"1,000"`).
     *   • `raw`       — unrounded numeric value (`Number`). This is what
     *                   gets written into the share-link payload so a
     *                   prefilled visitor sees the same calculation.
     *   • `id`        — the deterministic share ID stamped on the wrapper
     *                   by `assignShareIds()` — a short base36 hash of the
     *                   JCR resource path. Same value for every visitor.
     *   • `prefix` / `suffix` — raw trimmed prefix/suffix text.
     *   • `eloquaFieldName` — Eloqua column this answer maps to (from the
     *                   input/slider dialog's Eloqua tab). Empty string
     *                   means "do not include in Eloqua submission".
     *   • `includeQuestion` — when true, the eloqua-modal sends
     *                   `Question - Answer`; when false, just `Answer`.
     */
    buildResults() {
        if (this.state.displayMode === 'onsubmit') {
            this.calculate();
        }

        const inputRows = this.elements.inputs.map((input) => this.buildResultRow(input, {
            wrapperSel: '.calc-input',
            labelSel: '.calc-input__label-text, .calc-input__label',
            prefixSel: '.calc-input__prefix',
            suffixSel: '.calc-input__suffix',
            getRaw: (el) => el.getAttribute('data-raw-value') ?? el.value,
        }));

        const sliderRows = this.elements.sliders.map((slider) => this.buildResultRow(slider, {
            wrapperSel: '.calc-slider',
            labelSel: '.calc-slider__label',
            prefixSel: '.calc-slider__prefix',
            suffixSel: '.calc-slider__suffix',
            getRaw: (el) => el.value,
        }));

        const multiselectRows = this.elements.multiselects.map(
            (group) => this.buildMultiselectRow(group),
        );

        return [...inputRows, ...sliderRows, ...multiselectRows];
    }

    /**
     * Build one results row for a `.calc-multiselect` group. The display
     * `value` / `formatted` is the comma-joined list of checked option labels
     * (what an author wants to see in results + Eloqua); `raw` is the
     * pipe-joined list of checked option VALUES, which `applyHashPayload()`
     * uses to restore the exact selection from a share URL.
     */
    buildMultiselectRow(group) {
        const labelEl = group.querySelector('.calc-multiselect__legend-text');
        const label = labelEl
            ? CalculatorContainer.cleanLabel(labelEl.textContent)
            : (group.getAttribute('data-field-name') || '');

        const checked = Array.from(
            group.querySelectorAll('.calc-multiselect__input:checked'),
        );
        const labels = checked.map((cb) => {
            const optLabel = cb
                .closest('.calc-multiselect__option')
                ?.querySelector('.calc-multiselect__option-label');
            return CalculatorContainer.cleanLabel(optLabel ? optLabel.textContent : cb.value);
        });
        const value = labels.join(', ');

        return {
            label,
            value,
            formatted: value,
            raw: checked.map((cb) => cb.value).join('|'),
            id: group.getAttribute('data-share-id') || group.getAttribute('data-field-name') || '',
            prefix: '',
            suffix: '',
            eloquaFieldName: group.getAttribute('data-elq-field') || '',
            includeQuestion: group.getAttribute('data-elq-include-question') === 'true',
        };
    }

    /**
     * Internal helper for `buildResults()`. Reads a single input or slider
     * field and produces one row:
     *   { label, value, formatted, raw, id, prefix, suffix,
     *     eloquaFieldName, includeQuestion }
     *
     * `eloquaFieldName` and `includeQuestion` come from the dialog's
     * Eloqua tab (per input/slider). They are the contract the
     * eloqua-modal uses to decide which calculator answers to send and
     * whether to prefix them with the question label.
     *
     * Selectors and the raw-value reader differ between the two field
     * families (input stashes raw values on `data-raw-value`; slider
     * always reads from `.value`); everything else is identical so it
     * lives here.
     */
    buildResultRow(field, opts) {
        const wrapper = field.closest(opts.wrapperSel);
        const labelEl = wrapper?.querySelector(opts.labelSel);
        const label = labelEl
            ? CalculatorContainer.cleanLabel(labelEl.textContent)
            : field.name;
        const prefix = (wrapper?.querySelector(opts.prefixSel)?.textContent || '').trim();
        const suffix = (wrapper?.querySelector(opts.suffixSel)?.textContent || '').trim();
        const formatKey = wrapper?.getAttribute('data-format') || 'integer';
        const num = parseFloat(opts.getRaw(field)) || 0;
        const formatted = formatNumber(num, formatKey, this.locale);
        return {
            label,
            value: CalculatorContainer.joinValue(prefix, formatted, suffix),
            formatted,
            raw: num,
            id: wrapper?.getAttribute('data-share-id') || field.name || '',
            prefix,
            suffix,
            eloquaFieldName: wrapper?.getAttribute('data-elq-field') || '',
            includeQuestion: wrapper?.getAttribute('data-elq-include-question') === 'true',
        };
    }

    static cleanLabel(text) {
        return text.replace(/[*?]/g, '').replace(/\s+/g, ' ').trim();
    }

    static joinValue(prefix, formatted, suffix) {
        // Spec: "$ 0" or "0 hrs" — single space between symbol and number when present
        const parts = [];
        if (prefix) parts.push(`${prefix.trim()} `);
        parts.push(formatted);
        if (suffix) parts.push(` ${suffix.trim()}`);
        return parts.join('').replace(/\s+/g, ' ').trim();
    }

    /**
     * Returns a string formatted as "Label: Value" lines (one per line)
     * suitable to drop straight into an Eloqua textarea/hidden field.
     */
    buildResultsText() {
        return this.buildResults().map(({ label, value }) => `${label}: ${value}`).join('\n');
    }

    /**
     * Build the JSON payload posted to the futprint calculator-submit endpoint
     * for analytics. Distinct from buildResults() / buildResultsText() which
     * feed the Eloqua submission. Sent in addition to (not in place of) the
     * Eloqua POST.
     *
     *   inputs[]  — one entry per user-entered field (input + slider).
     *   results[] — one entry per computed display.
     *
     * `id` is the field's `name` attribute for inputs/sliders and
     * `data-field-name` for displays. No fallback — if a field has no name
     * it's an authoring bug and is skipped.
     */
    buildCalculatorPayload({ eloquaID = '' } = {}) {
        if (this.state.displayMode === 'onsubmit') {
            this.calculate();
        }

        const inputs = [];
        this.elements.inputs.forEach((input) => {
            const id = input.getAttribute('name');
            if (!id) return;
            const raw = input.getAttribute('data-raw-value');
            const value = raw !== null ? raw : input.value;
            inputs.push({ id, value: String(value) });
        });
        this.elements.sliders.forEach((slider) => {
            const id = slider.getAttribute('name');
            if (!id) return;
            inputs.push({ id, value: String(slider.value) });
        });
        this.elements.multiselects.forEach((group) => {
            const id = group.getAttribute('data-field-name');
            if (!id) return;
            const value = Array.from(
                group.querySelectorAll('.calc-multiselect__input:checked'),
            ).map((cb) => cb.value).join(', ');
            inputs.push({ id, value });
        });

        const results = [];
        this.elements.displays.forEach((display) => {
            const id = display.getAttribute('data-field-name');
            if (!id) return;
            const numeric = display.getAttribute('data-numeric-value');
            results.push({ id, value: numeric !== null ? numeric : '0' });
        });

        return {
            submission_type: 'calculator',
            parentFlowPath: this.element.getAttribute('data-flow-path') || '',
            calculator_name: this.element.getAttribute('data-calculator-name') || '',
            eloquaID,
            timestamp: Date.now(),
            inputs,
            results,
        };
    }

    /**
     * Encode the current form state (id ⇄ raw-numeric pairs) into an
     * LZ-compressed, URL-safe string suitable for use as a URL hash.
     *
     * We use ASCII RS/US separators rather than `&`/`=` so the LZ pass
     * compresses the framing characters away — they appear once in the
     * dictionary regardless of how many fields we encode. The output is
     * already URI-safe (lz-string's `compressToEncodedURIComponent`),
     * so it can be dropped straight into `location.hash` with no extra
     * `encodeURIComponent` wrapping.
     */
    encodeCompactFormPayloadString() {
        const results = this.buildResults();
        const pairs = results
            .filter((r) => r.id)
            .map((r) => `${r.id}${PAYLOAD_KV_SEP}${r.raw}`);
        const raw = pairs.join(PAYLOAD_FIELD_SEP);
        const compressed = lzString.compressToEncodedURIComponent(raw);
        if (compressed.length > PAYLOAD_WARN_LENGTH) {
            // eslint-disable-next-line no-console
            console.warn(`Calculator: compressed share payload is ${compressed.length} chars (>${PAYLOAD_WARN_LENGTH}). The shared URL may be too long for some browsers/servers.`);
        }
        return compressed;
    }

    /**
     * Build the URL hash fragment for this calculator's share link.
     *
     * Format: `cid=<containerId>;<lz-payload>` when the container has
     * a containerId set (the standard authoring path on multi-calculator
     * pages); falls back to just `<lz-payload>` when no containerId is
     * authored (single-calculator pages and legacy authoring).
     *
     * Pages with multiple Calculator Containers MUST author a
     * containerId on each — that's the only way the prefill code can
     * tell which calculator owns the values.
     */
    getShareUrlHash() {
        const payload = this.encodeCompactFormPayloadString();
        if (!payload) return '';
        if (this.containerId) {
            return `${HASH_CID_PREFIX}${this.containerId}${HASH_CID_SEP}${payload}`;
        }
        return payload;
    }

    /**
     * Split a raw URL hash into `{ containerId, payload }`. Accepts the
     * new `cid=<id>;<lz>` format and the legacy plain `<lz>` format.
     * `containerId` is empty when the hash doesn't carry one — callers
     * treat that as "any container is allowed to prefill".
     */
    static parseShareUrlHash(hash) {
        const cleaned = (hash || '').replace(/^#/, '');
        if (!cleaned) return { containerId: '', payload: '' };
        if (cleaned.startsWith(HASH_CID_PREFIX)) {
            const sepIdx = cleaned.indexOf(HASH_CID_SEP);
            if (sepIdx > HASH_CID_PREFIX.length) {
                return {
                    containerId: cleaned.slice(HASH_CID_PREFIX.length, sepIdx),
                    payload: cleaned.slice(sepIdx + 1),
                };
            }
        }
        return { containerId: '', payload: cleaned };
    }

    /**
     * Inverse of `encodeCompactFormPayloadString`. Returns an array of
     * `{ id, value }` pairs (value is a string — caller decides how to
     * coerce). Returns `[]` for any input that isn't a valid LZ payload
     * so a stray hash never throws.
     */
    static decodeCompactFormPayloadString(encoded) {
        const raw = lzString.decompressFromEncodedURIComponent(encoded);
        if (!raw) return [];
        return raw.split(PAYLOAD_FIELD_SEP).map((pair) => {
            const idx = pair.indexOf(PAYLOAD_KV_SEP);
            if (idx < 0) return null;
            return { id: pair.slice(0, idx), value: pair.slice(idx + 1) };
        }).filter(Boolean);
    }

    /**
     * If the current URL hash carries a share payload AND it's addressed
     * to this container (or has no `cid=` prefix at all), decode it and
     * push each value into the matching input/slider (looked up by the
     * deterministic share ID stamped on the wrapper). Then run the
     * calculator, mark this controller as `isPrefilled` so the eloqua
     * modal can short-circuit submit, and switch the page into
     * "review" presentation: the results page lands at the top with
     * the read-only, fully-expanded form below it (see
     * `enterPrefilledReviewMode()`).
     *
     * Silently no-ops if the hash is empty, decompression fails, no
     * field IDs match, or the `cid=` prefix points at a different
     * container on the page.
     */
    applyHashPayload() {
        const { containerId: targetId, payload } = CalculatorContainer.parseShareUrlHash(window.location.hash);
        if (!payload) return;

        /* Multi-calculator pages: each container only consumes payloads
           addressed to its own ID. Legacy hashes without a `cid=` prefix
           still apply (single-calculator pages), so existing share URLs
           keep working. */
        if (targetId && this.containerId && targetId !== this.containerId) return;

        const entries = CalculatorContainer.decodeCompactFormPayloadString(payload);
        if (!entries.length) return;

        let applied = 0;
        entries.forEach(({ id, value }) => {
            if (!id) return;
            const safe = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(id) : id;
            const wrapper = this.element.querySelector(
                `.calc-input[data-share-id="${safe}"], .calc-slider[data-share-id="${safe}"], .calc-multiselect[data-share-id="${safe}"]`,
            );
            if (!wrapper) return;

            /* Multiselect: `value` is the pipe-joined list of checked option
               values (see buildMultiselectRow). Restore each matching option. */
            if (wrapper.classList.contains('calc-multiselect')) {
                const selected = new Set(value ? value.split('|') : []);
                wrapper.querySelectorAll('.calc-multiselect__input').forEach((cb) => {
                    cb.checked = selected.has(cb.value); // eslint-disable-line no-param-reassign
                });
                this.enforceMaxSelections(wrapper);
                applied += 1;
                return;
            }

            const field = wrapper.querySelector('.calc-input__field, .calc-slider__range');
            if (!field) return;

            const num = value !== '' ? Number(value) : NaN;
            if (!Number.isFinite(num)) return;

            if (field.classList.contains('calc-slider__range')) {
                field.value = num; // eslint-disable-line no-param-reassign
                updateSliderFill(field);
                updateSliderDisplay(field, this.locale);
            } else {
                const formatKey = wrapper.getAttribute('data-format') || 'integer';
                field.setAttribute('data-raw-value', String(num));
                if (formatKey !== 'plain') {
                    field.type = 'text'; // eslint-disable-line no-param-reassign
                    field.value = formatNumber(num, formatKey, this.locale); // eslint-disable-line no-param-reassign
                } else {
                    field.value = num; // eslint-disable-line no-param-reassign
                }
            }
            applied += 1;
        });

        if (!applied) return;

        this.calculate();
        this.isPrefilled = true;
        this.enterPrefilledReviewMode();
    }

    /**
     * Lock the calculator into "review" presentation triggered by a
     * successful share-URL prefill (`applyHashPayload()`).
     *
     *   • Renders the results page and reveals it (CSS reorders the
     *     container so the results view sits above the form).
     *   • Expands every section so totals and display fields are
     *     visible at once — even in `displayMode = "onsubmit"`, where
     *     the displays / section totals are normally hidden until
     *     submit. The CSS opt-out keys off `data-prefilled="true"`.
     *   • Marks every input as readonly and every slider as disabled
     *     so the shared values cannot be tampered with.
     *   • Hides the per-section "Next" CTAs and any Submit / Reset /
     *     Eloqua-modal CTAs on the form view (CSS, not JS, since
     *     those components live in a separate parsys).
     */
    enterPrefilledReviewMode() {
        this.element.setAttribute('data-prefilled', 'true');

        this.elements.sections.forEach((section) => {
            section.classList.add('calc-section--open');
            const header = section.querySelector('.calc-section__header');
            if (header) header.setAttribute('aria-expanded', 'true');
        });

        this.elements.inputs.forEach((input) => {
            input.readOnly = true; // eslint-disable-line no-param-reassign
            input.tabIndex = -1; // eslint-disable-line no-param-reassign
        });
        this.elements.sliders.forEach((slider) => {
            slider.disabled = true; // eslint-disable-line no-param-reassign
            slider.tabIndex = -1; // eslint-disable-line no-param-reassign
        });
        this.elements.multiselects.forEach((group) => {
            group.querySelectorAll('.calc-multiselect__input').forEach((cb) => {
                cb.disabled = true; // eslint-disable-line no-param-reassign
                cb.tabIndex = -1; // eslint-disable-line no-param-reassign
                cb.setAttribute('data-review-disabled', 'true');
            });
        });

        this.renderResultsView();
        if (this.elements.resultsView) {
            this.elements.resultsView.hidden = false;
            this.scrollResultsViewToTop();
        }

        this.notifyActivated();
    }

    /**
     * Tell any Calculator Selector on the page that this container is
     * the active one (so it can hide itself + reveal this container).
     * Also unhides the container itself in case the selector ran first
     * and pre-hid every container by default. Used by both the post-
     * submit results path (`showResultsView()`) and the share-URL
     * prefill path (`enterPrefilledReviewMode()`).
     */
    notifyActivated() {
        this.element.hidden = false;
        document.dispatchEvent(new CustomEvent('calc-selector:activate', {
            detail: { containerId: this.containerId },
        }));
    }

    /**
     * Tear down the read-only review mode set up by
     * `enterPrefilledReviewMode()`. Re-enables every input/slider,
     * collapses sections back to the single-open default with the
     * first section open, and strips the share payload off the URL
     * so a refresh doesn't drop the visitor right back into review.
     *
     * Triggered by the results page's "Start Over" button via
     * `resetAll()`.
     */
    exitPrefilledReviewMode() {
        this.element.removeAttribute('data-prefilled');
        this.isPrefilled = false;

        this.elements.inputs.forEach((input) => {
            input.readOnly = false; // eslint-disable-line no-param-reassign
            input.removeAttribute('tabindex');
        });
        this.elements.sliders.forEach((slider) => {
            slider.disabled = false; // eslint-disable-line no-param-reassign
            slider.removeAttribute('tabindex');
        });
        this.elements.multiselects.forEach((group) => {
            group.querySelectorAll('.calc-multiselect__input').forEach((cb) => {
                cb.disabled = false; // eslint-disable-line no-param-reassign
                cb.removeAttribute('tabindex');
                cb.removeAttribute('data-review-disabled');
            });
            this.enforceMaxSelections(group);
        });

        this.setInitialSectionState();

        if (window.location.hash) {
            const cleanUrl = window.location.pathname + window.location.search;
            window.history.replaceState(null, '', cleanUrl);
        }
    }

    /**
     * Build the breakdown data tree consumed by `renderResultsView()`.
     *
     * Shape:
     * ```
     * {
     *   categories: [
     *     {
     *       key, label, description, summaryEl, columns: [{ key, label }],
     *       sections: [{
     *         id, title,
     *         valuesByColumn: { [colKey]: { numeric, formatted } },
     *       }],
     *     },
     *   ],
     * }
     * ```
     *
     * Authoring contract:
     *   • Categories come from non-highlighted Summary Bar items (the
     *     highlighted item is the hero — excluded from the breakdown).
     *     Each summary item authors `resultColumns` (key + label, ordered).
     *   • Each Calculator Section's `data-category` must match a summary
     *     item's `fieldName` to appear in that category's table.
     *   • Each Display authors `columnKey`; if it matches one of the
     *     parent section's category columns, that display's value lands
     *     in that column.
     *
     * Calculators are now scoped per-Container — there is no cross-tab /
     * section-group grouping. Pages that previously used Operations /
     * Maintenance section groups should split into TWO Calculator
     * Containers (paired with a Calculator Selector), so each container's
     * breakdown is naturally just its own sections.
     */
    buildBreakdown() {
        /* ── 1. Categories from non-highlighted summary items ── */
        const categories = [];
        const categoryByKey = {};

        this.elements.summaryValues.forEach((summaryEl) => {
            if (summaryEl.getAttribute('data-summary-highlighted') === 'true') return;

            const key = (summaryEl.getAttribute('data-summary-field') || '').trim();
            if (!key) return;

            const item = summaryEl.closest('.calc-summary__item');
            const columns = item
                ? Array.from(item.querySelectorAll('.calc-summary__column')).map((col) => ({
                    key: (col.getAttribute('data-column-key') || '').trim(),
                    label: (col.getAttribute('data-column-label') || '').trim(),
                })).filter((c) => c.key)
                : [];

            const description = CalculatorContainer.getSummaryDescription(summaryEl);

            const category = {
                key,
                label: summaryEl.getAttribute('data-summary-label') || key,
                description,
                summaryEl,
                columns,
                sections: [],
            };
            categories.push(category);
            categoryByKey[key] = category;
        });

        /* ── 2. Drop each Calculator Section into the matching category. ── */
        this.elements.sections.forEach((sectionEl, index) => {
            const categoryKey = (sectionEl.getAttribute('data-category') || '').trim();
            const category = categoryByKey[categoryKey];
            if (!category) return;

            const titleEl = sectionEl.querySelector('.calc-section__title');
            const title = titleEl
                ? CalculatorContainer.cleanLabel(titleEl.textContent)
                : '';

            /* Map each display's column-key to {numeric, formatted}. */
            const valuesByColumn = {};
            sectionEl.querySelectorAll('.calc-display[data-column-key]').forEach((displayEl) => {
                const colKey = (displayEl.getAttribute('data-column-key') || '').trim();
                if (!colKey) return;
                const numeric = parseFloat(displayEl.getAttribute('data-numeric-value')) || 0;
                const valueEl = displayEl.querySelector('.calc-display__value');
                const formatted = valueEl
                    ? (valueEl.textContent || '').trim()
                    : this.formatValue(numeric, displayEl);
                valuesByColumn[colKey] = { numeric, formatted };
            });

            category.sections.push({
                id: sectionEl.getAttribute('data-share-id') || `section-${index}`,
                title,
                valuesByColumn,
            });
        });

        return { categories };
    }

    /* ───── Results View (public API) ───── */

    /**
     * Scroll the window so the top of `.calc-container__results-view` sits
     * at the viewport top. Runs again after paint and on the next task
     * because `<dialog>.close()` restores focus asynchronously and the
     * browser may scroll to a control below the fold, leaving results at the
     * bottom of the viewport.
     */
    scrollResultsViewToTop() {
        const { resultsView } = this.elements;
        if (!resultsView || resultsView.hidden) return;

        const alignTop = () => {
            const y = resultsView.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: Math.max(0, y), left: 0, behavior: 'auto' });
        };

        alignTop();
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(alignTop);
        });
        window.setTimeout(alignTop, 0);
    }

    /**
     * Hide the form and render the post-submit results page.
     * Called by eloqua-modal after a successful submission.
     */
    showResultsView() {
        this.calculate();
        this.renderResultsView();

        if (this.elements.formView) {
            this.elements.formView.hidden = true;
        }
        const { resultsView } = this.elements;
        if (resultsView) {
            resultsView.hidden = false;
            this.scrollResultsViewToTop();
        }

        this.notifyActivated();
    }

    /**
     * Render the post-submit results page (Figma node 404:2082).
     *
     * Layout (top to bottom):
     *   1. Toolbar — Start Over (resets) + Print Report (window.print).
     *   2. Hero card — highlighted summary item shown big and dark-blue,
     *      with its description and a Contact CTA.
     *   3. Category rows — one per non-highlighted summary item; each row
     *      is a left-side card (big number + label) paired with a right-
     *      side description column.
     *   4. Toggle link — "See cost savings breakdown" (chevron flips on open).
     *   5. Hidden-by-default breakdown panel — one H3 + Benefit + author-
     *      defined-columns table per non-highlighted category. All sections
     *      in this calculator feed the table for their category; pages
     *      that need an Operations / Maintenance split should use two
     *      Calculator Containers gated by a Calculator Selector instead.
     */
    renderResultsView() {
        const view = this.elements.resultsView;
        if (!view) return;

        const cfg = {
            ctaShow: view.getAttribute('data-breakdown-cta-show') || 'See cost savings breakdown',
            ctaHide: view.getAttribute('data-breakdown-cta-hide') || 'Hide cost savings breakdown',
            colBenefit: view.getAttribute('data-breakdown-benefit-label') || 'Benefit',
            startOver: view.getAttribute('data-start-over-label') || 'Start Over',
            printLabel: view.getAttribute('data-print-label') || 'Print Report',
            contactLbl: view.getAttribute('data-contact-cta-label') || 'Contact a Rockwell Specialist',
            contactHref:
                view.getAttribute('data-contact-cta-href')
                || view.getAttribute('data-contact-cta-url')
                || '',
            /* Share-URL review: hero Contact must not navigate (visitor is
               viewing someone else's scenario — same read-only contract as
               the form view below). */
            prefilledReadonly: this.isPrefilled,
        };

        const breakdown = this.buildBreakdown();

        const heroEl = this.elements.summaryValues.find(
            (el) => el.getAttribute('data-summary-highlighted') === 'true',
        );

        const heroHtml = this.renderHeroCard(heroEl, cfg);
        const categoryRowsHtml = breakdown.categories
            .map((category) => this.renderCategoryRow(category))
            .join('');
        const breakdownHtml = this.renderBreakdownCategories(breakdown, cfg);

        view.innerHTML = `
            <div class="calc-results-page">
                <div class="calc-results-page__toolbar">
                    <button type="button" class="calc-results-page__btn calc-results-page__btn--secondary" data-action="start-over">
                        <svg class="calc-results-page__btn-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path fill="currentColor" d="M5.44313e-08 8C5.44313e-08 12.4115 3.49894 16 7.79966 16C11.3823 16 14.4886 13.5156 15.3519 9.97002C15.3559 9.91471 15.3665 9.86456 15.3771 9.81184C15.3857 9.77152 15.3932 9.73585 15.3932 9.70277C15.3932 9.47997 15.2521 9.32437 15.052 9.32437C14.8237 9.32437 14.6867 9.46084 14.6413 9.74102L14.6181 9.80822C14.2288 11.3598 13.3477 12.7346 12.1134 13.7163C10.8791 14.698 9.36157 15.231 7.79966 15.2313C3.91218 15.2313 0.749376 11.9876 0.749376 8.00052C0.749376 4.01292 3.91218 0.769191 7.79966 0.769191C10.3638 0.769191 12.7278 2.20522 13.9695 4.51693L14.2346 5.01008L12.7994 4.4213C12.7284 4.40028 12.6529 4.40119 12.5824 4.42392C12.5119 4.44665 12.4495 4.49019 12.403 4.54909C12.3565 4.608 12.3279 4.67966 12.3209 4.75513C12.3138 4.83059 12.3287 4.9065 12.3635 4.97338C12.3882 5.01965 12.4219 5.06029 12.4624 5.09281C12.503 5.12533 12.5495 5.14907 12.5993 5.16257L14.8812 6.0398C14.9755 6.06745 15.0765 6.05644 15.1631 6.00908C15.2496 5.96172 15.3147 5.88173 15.3448 5.78599L15.9843 3.29801C15.9986 3.24944 16.0032 3.19844 15.9979 3.14802C15.9926 3.09759 15.9775 3.04876 15.9536 3.00439C15.9216 2.94356 15.8742 2.89274 15.8164 2.85733C15.7585 2.82193 15.6924 2.80326 15.625 2.80331C15.5454 2.8031 15.4677 2.82895 15.4034 2.87711C15.339 2.92526 15.2913 2.99321 15.2672 3.07108L14.8807 4.63634L14.6418 4.18506C13.9739 2.92037 12.9856 1.86402 11.7812 1.12744C10.5768 0.390857 9.20089 0.0013489 7.79866 0C3.49843 0 -0.000503897 3.58852 5.44313e-08 8Z"/>
                        </svg>
                        <span>${CalculatorContainer.escape(cfg.startOver)}</span>
                    </button>
                    <button type="button" class="calc-results-page__btn calc-results-page__btn--primary" data-action="print">
                        <span>${CalculatorContainer.escape(cfg.printLabel)}</span>
                    </button>
                </div>
                ${heroHtml}
                <div class="calc-results-page__categories">
                    ${categoryRowsHtml}
                </div>
                ${breakdownHtml ? `
                <button type="button"
                        class="calc-results-page__breakdown-toggle"
                        aria-expanded="false"
                        data-show-label="${CalculatorContainer.escape(cfg.ctaShow)}"
                        data-hide-label="${CalculatorContainer.escape(cfg.ctaHide)}">
                    <span class="calc-results-page__breakdown-toggle-label">${CalculatorContainer.escape(cfg.ctaShow)}</span>
                    <svg class="calc-results-page__breakdown-toggle-icon" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                        <polyline points="2 4 6 8 10 4"/>
                    </svg>
                </button>
                <div class="calc-results-page__breakdown" hidden>
                    ${breakdownHtml}
                </div>
                ` : ''}
            </div>
        `;

        this.bindResultsActions(view);
        CalculatorContainer.reloadGatedContentForHeroContact(view);
    }

    /* ───── Results-view helpers ───── */

    /**
     * Resolve the authored description for a Summary Bar item. Prefers
     * the hidden `<div class="calc-summary__description">` child (which
     * preserves whitespace exactly as the author typed it) and falls
     * back to the `data-summary-description` attribute on the value
     * element. Returns '' when neither is present so callers can guard
     * the surrounding wrapper.
     */
    static getSummaryDescription(summaryEl) {
        if (!summaryEl) return '';
        const item = summaryEl.closest('.calc-summary__item');
        const descEl = item ? item.querySelector('.calc-summary__description') : null;
        if (descEl) return (descEl.textContent || '').trim();
        return summaryEl.getAttribute('data-summary-description') || '';
    }

    /**
     * Resolve the score band for a summary item given its computed score.
     * Returns the first `.calc-summary__band` whose `[data-min, data-max]`
     * range (both inclusive; either bound optional) contains the score, or
     * `null` when the item has no bands or none match. This is what turns a
     * numeric summary item into a scored self-assessment result.
     */
    static resolveBand(summaryEl, score) {
        if (!summaryEl) return null;
        const item = summaryEl.closest('.calc-summary__item');
        if (!item) return null;
        const bands = Array.from(item.querySelectorAll('.calc-summary__band'));
        if (!bands.length) return null;
        return bands.find((band) => {
            const min = parseFloat(band.getAttribute('data-min'));
            const max = parseFloat(band.getAttribute('data-max'));
            const aboveMin = !Number.isFinite(min) || score >= min;
            const belowMax = !Number.isFinite(max) || score <= max;
            return aboveMin && belowMax;
        }) || null;
    }

    /**
     * Read a band's authored body / next-steps copy from its hidden child
     * node (the child preserves authored newlines; see container.html).
     */
    static bandText(bandEl, selector) {
        const el = bandEl ? bandEl.querySelector(selector) : null;
        return el ? (el.textContent || '').trim() : '';
    }

    /**
     * Render an authored multi-paragraph description as one or more `<p>`
     * elements with the given BEM class. Splits on one-or-more blank lines,
     * trims each paragraph, escapes the text, and turns single newlines
     * within a paragraph into `<br>`. Returns '' when the description is
     * empty so the caller can guard the surrounding wrapper.
     */
    static renderDescriptionParagraphs(text, className) {
        if (!text || !text.trim()) return '';
        const paragraphs = text
            .replace(/\r\n/g, '\n')
            .split(/\n{2,}/)
            .map((p) => p.trim())
            .filter(Boolean);
        return paragraphs.map((p) => {
            const escaped = CalculatorContainer.escape(p).replace(/\n/g, '<br>');
            return `<p class="${className}">${escaped}</p>`;
        }).join('');
    }

    renderHeroCard(heroEl, cfg) {
        const value = heroEl ? heroEl.textContent.trim() : '';

        /* Scored self-assessment: when the hero summary item has score bands,
           the hero renders the band matching the computed score (headline +
           interpretation copy + next steps) instead of the numeric summary
           description. The formatted score itself (e.g. "14 / 20") still shows
           as the hero stat. Falls back to the normal numeric hero when there
           are no bands, so the ROI calculator is unaffected. */
        const score = heroEl ? parseFloat(heroEl.getAttribute('data-numeric-value')) || 0 : 0;
        const band = CalculatorContainer.resolveBand(heroEl, score);

        let label;
        let bodyHtml;
        let nextStepsHtml = '';
        if (band) {
            label = band.getAttribute('data-headline') || '';
            bodyHtml = CalculatorContainer.renderDescriptionParagraphs(
                CalculatorContainer.bandText(band, '.calc-summary__band-body'),
                'calc-results-page__hero-description',
            );
            const nextSteps = CalculatorContainer.bandText(band, '.calc-summary__band-next-steps');
            if (nextSteps) {
                const heading = band.getAttribute('data-next-steps-heading') || 'What to do next';
                nextStepsHtml = `
                    <div class="calc-results-page__hero-next-steps">
                        <div class="calc-results-page__hero-next-steps-heading">${CalculatorContainer.escape(heading)}</div>
                        ${CalculatorContainer.renderDescriptionParagraphs(nextSteps, 'calc-results-page__hero-description')}
                    </div>
                `;
            }
        } else {
            label = heroEl ? (heroEl.getAttribute('data-summary-label') || '') : '';
            bodyHtml = CalculatorContainer.renderDescriptionParagraphs(
                CalculatorContainer.getSummaryDescription(heroEl),
                'calc-results-page__hero-description',
            );
        }

        const copyHtml = (bodyHtml || nextStepsHtml)
            ? `<div class="calc-results-page__hero-copy">${bodyHtml}${nextStepsHtml}</div>`
            : '';

        const linkAttrs = ` data-link-type="roi-calculator" data-link-text="${CalculatorContainer.escape(cfg.contactLbl)}"`;
        let ctaButton;
        if (cfg.contactHref) {
            if (cfg.prefilledReadonly) {
                ctaButton = `<div class="calc-results-page__btn calc-results-page__btn--primary calc-results-page__btn--large calc-results-page__btn--contact-readonly" role="button" aria-disabled="true" tabindex="-1"><span>${CalculatorContainer.escape(cfg.contactLbl)}</span></div>`;
            } else {
                ctaButton = `<a class="calc-results-page__btn calc-results-page__btn--primary calc-results-page__btn--large"${linkAttrs} href="${CalculatorContainer.escape(cfg.contactHref)}"><span>${CalculatorContainer.escape(cfg.contactLbl)}</span></a>`;
            }
        } else {
            ctaButton = `<button type="button" class="calc-results-page__btn calc-results-page__btn--primary calc-results-page__btn--large"${linkAttrs}><span>${CalculatorContainer.escape(cfg.contactLbl)}</span></button>`;
        }

        return `
            <div class="calc-results-page__hero">
                <div class="calc-results-page__hero-stat">
                    <div class="calc-results-page__hero-value">${CalculatorContainer.escape(value)}</div>
                    <div class="calc-results-page__hero-label">${CalculatorContainer.escape(label)}</div>
                    ${copyHtml}
                </div>
                ${ctaButton}
            </div>
        `;
    }

    renderCategoryRow(category) {
        const value = category.summaryEl.textContent.trim();
        const { label } = category;

        const descriptionHtml = CalculatorContainer.renderDescriptionParagraphs(
            category.description,
            'calc-results-page__category-paragraph',
        );

        return `
            <div class="calc-results-page__category-row">
                <div class="calc-results-page__category-card">
                    <div class="calc-results-page__category-value">${CalculatorContainer.escape(value)}</div>
                    <div class="calc-results-page__category-label">${CalculatorContainer.escape(label)}</div>
                </div>
                <div class="calc-results-page__category-description">${descriptionHtml}</div>
            </div>
        `;
    }

    /**
     * Author-driven breakdown panel — flat, one block per non-highlighted
     * category:
     *   <h3 class="…__category-title">category label</h3>
     *   <table>
     *     <thead>Benefit | category's own columns</thead>
     *     <tbody>one row per section, only that category's columns</tbody>
     *   </table>
     *
     * Column alignment: every table uses the SAME value-column width
     * (60% / maxValueCols across the page's categories), so even when
     * category schemas differ ("Cost / Sales" vs "Cost / Savings") the
     * column edges line up pixel-for-pixel down the page. `table-layout:
     * fixed` in container.less + the explicit per-table `<colgroup>`
     * widths emitted below make the widths win over content-based
     * sizing. Tables with fewer columns leave dead space on the right.
     *
     * Categories with no `resultColumns` are skipped — that's the
     * authoring contract for the highlighted hero (which lives on the
     * hero card and has no breakdown table).
     */
    renderBreakdownCategories(breakdown, cfg) {
        const printable = breakdown.categories.filter(
            (category) => category.columns && category.columns.length > 0,
        );
        if (printable.length === 0) return '';

        /* Width-share across categories so adjacent tables have aligned
           column edges (see method docblock). */
        const maxValueCols = printable.reduce(
            (max, category) => Math.max(max, category.columns.length),
            0,
        );
        const valueColPct = (60 / maxValueCols).toFixed(4);

        return printable.map((category) => {
            const colgroup = [
                '<col class="calc-results-page__benefit-col" style="width:40%">',
                ...category.columns.map(() => `<col style="width:${valueColPct}%">`),
            ].join('');

            const headerCells = [
                `<th scope="col" class="calc-results-page__benefit-col">${CalculatorContainer.escape(cfg.colBenefit)}</th>`,
                ...category.columns.map((col) => `<th scope="col">${CalculatorContainer.escape(col.label)}</th>`),
            ].join('');

            const bodyRows = category.sections.map((row) => {
                const cells = [
                    `<th scope="row">${CalculatorContainer.escape(row.title)}</th>`,
                    ...category.columns.map((col) => {
                        const cell = row.valuesByColumn[col.key];
                        const text = cell ? cell.formatted : '';
                        return `<td>${CalculatorContainer.escape(text)}</td>`;
                    }),
                ].join('');
                return `<tr>${cells}</tr>`;
            }).join('');

            return `
                <div class="calc-results-page__category-block">
                    <h3 class="calc-results-page__category-title">${CalculatorContainer.escape(category.label)}</h3>
                    <table class="calc-results-page__benefit-table">
                        <colgroup>${colgroup}</colgroup>
                        <thead><tr>${headerCells}</tr></thead>
                        <tbody>${bodyRows}</tbody>
                    </table>
                </div>
            `;
        }).join('');
    }

    bindResultsActions(view) {
        const startOverBtn = view.querySelector('[data-action="start-over"]');
        if (startOverBtn) {
            startOverBtn.addEventListener('click', () => {
                this.resetAll();
                if (this.elements.formView) this.elements.formView.hidden = false;
                view.hidden = true; // eslint-disable-line no-param-reassign

                /* Multi-calculator pages: hand control back to the
                   Calculator Selector if one is on the page so the
                   visitor can pick a different calculator. The Selector
                   listens for this event, hides every known container,
                   and unhides itself. Single-calculator pages just stay
                   on the form view of this container — the event has
                   no listener and is a no-op. */
                document.dispatchEvent(new CustomEvent('calc-selector:reopen', {
                    detail: { containerId: this.containerId },
                }));

                this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }

        const printBtn = view.querySelector('[data-action="print"]');
        if (printBtn) {
            printBtn.addEventListener('click', () => window.print());
        }

        const toggle = view.querySelector('.calc-results-page__breakdown-toggle');
        const breakdownEl = view.querySelector('.calc-results-page__breakdown');
        const labelEl = toggle?.querySelector('.calc-results-page__breakdown-toggle-label');
        if (toggle && breakdownEl && labelEl) {
            toggle.addEventListener('click', () => {
                const isOpen = !breakdownEl.hasAttribute('hidden');
                if (isOpen) {
                    breakdownEl.setAttribute('hidden', '');
                    toggle.setAttribute('aria-expanded', 'false');
                    labelEl.textContent = toggle.getAttribute('data-show-label') || '';
                } else {
                    breakdownEl.removeAttribute('hidden');
                    toggle.setAttribute('aria-expanded', 'true');
                    labelEl.textContent = toggle.getAttribute('data-hide-label') || '';
                }
                toggle.classList.toggle('calc-results-page__breakdown-toggle--open', !isOpen);
            });
        }
    }

    /**
     * Basepage runs `gcdc('loadGates')` on `window.load` before calculator
     * results exist. Hero Contact is injected later via `innerHTML`, so the
     * first pass never binds that anchor — clicks only change `location.hash`.
     * Re-run `loadGates` after mount (same pattern as sales-contact teaser in
     * webpack/util/js/ra.js).
     *
     * @param {HTMLElement} root — `.calc-container__results-view` (or any
     *        subtree containing `.calc-results-page__hero`).
     */
    static reloadGatedContentForHeroContact(root) {
        if (!root || typeof root.querySelector !== 'function') return;
        const contactAnchor = root.querySelector(
            '.calc-results-page__hero a.calc-results-page__btn--primary',
        );
        if (!contactAnchor) return;

        let href = (contactAnchor.getAttribute('href') || '').trim();
        if (href.startsWith('gate-')) {
            href = `#${href}`;
            contactAnchor.setAttribute('href', href);
        }
        if (!href.startsWith('#gate-')) return;

        const gatedContentName = window.GatedContentObject || 'gcdc';
        const gatedContentObject = window[gatedContentName];
        if (typeof gatedContentObject === 'function') {
            gatedContentObject('loadGates');
        }
    }

    static escape(str) {
        if (str === null || str === undefined) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}

registerComponent(`.${COMPONENT}`, CalculatorContainer);
